import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable, throwError, of} from 'rxjs';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {switchMap, tap, catchError} from 'rxjs/operators';

import {AuthorizationService} from './authorization.service';
import {Conversation} from '../models/chat';
import {environment} from '../environments/enviroment';

@Injectable({
  providedIn: 'root'
})
export class CustomerConversationService {

  // api
  private domain: string = `${environment.mindxbkUrl}CustomerConversation/Conversation/`;

  // BehaviorSubject
  private conversation_subject: BehaviorSubject<Conversation | null> = new BehaviorSubject<Conversation | null>(null);
  conversation$: Observable<Conversation | null> = this.conversation_subject.asObservable();


  constructor(
    private http: HttpClient,
    private authService: AuthorizationService
  ) {
    // Remove automatic connectivity monitoring setup
    // this.setupConnectivityHandling();
  }

  setConversation(conversation: Conversation | null) {
    if (!conversation) return;
    // Always emit the provided conversation object so consumers receive updated fields
    this.conversation_subject.next(conversation);
  }

  resetConversation() {
    this.conversation_subject.next(null)
  }

  getConversationAvailableAgent(agentId: string): Observable<Conversation[]> {
    const token = localStorage.getItem('auth_req');
    if (!token) {
      console.error('❌ No se encontró token en localStorage (auth_req)');
      return throwError(() => new Error('No auth token available'));
    }

    const url = `${this.domain}Conversations/${agentId}`;
    const headers = new HttpHeaders({
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    });

    return this.http.get<Conversation[]>(url, { headers });
  }


  getActiveConversations(agentId: string) {
    const token = localStorage.getItem('auth_req') || '';
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${token}`,
      'Accept': '*/*'
    });
    const url = `${environment.mindxbkUrl}CustomerConversation/Conversation/Conversations/${agentId}/Active`;
    return this.http.get<Conversation[]>(url, { headers });
  }

  transferConversationCampaign(
    conversationId: string,
    campaignId: string
  ): Observable<any> {
    const token = localStorage.getItem('auth_req');
    if (!token) {
      console.error('❌ No se encontró token en localStorage (auth_req)');
      return throwError(() => new Error('No auth token available'));
    }

    const url = `${this.domain}AgentTransferedToCampaign`;
    const headers = new HttpHeaders({
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    });
    const body = {
      ConversationId: conversationId,
      CampaignId: campaignId
    };

    return this.http.post<any>(url, body, { headers });
  }

  transferToGroup(
    conversationId: string,
    campaignName: string,
    keyPrefix?: string
  ): Observable<any> {
    const token = localStorage.getItem('auth_req');
    if (!token) {
      console.error('No se encontró token en localStorage (auth_req)');
      return throwError(() => new Error('No auth token available'));
    }

    const url = `${environment.apiUrl}agent/transferToGroup`;
    const headers = new HttpHeaders({
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    });

    const now = new Date();

    // getTime() da milisegundos UTC
    // offset -5 horas → -5 * 60 * 60 * 1000
    const utcMinus5 = new Date(now.getTime() - (5 * 60 * 60 * 1000));

    // ISO string sin la Z final para que no confunda con UTC+0
    const formatted = utcMinus5.toISOString().replace('Z', '');

    const body = {
      conversationId: conversationId,
      keyPrefix: keyPrefix,
      transfer: {
        conversationTransfered: utcMinus5.toISOString().replace('Z', ''),
        transferCampaing: campaignName,
      },
    };

    return this.http.post<any>(url, body, { headers });
  }

  patchConversationTicket(conversationId: string, ticketId: string): Observable<any> {
    const token = localStorage.getItem('auth_req');
    if (!token) {
      console.error('❌ No se encontró token en localStorage (auth_req)');
      return throwError(() => new Error('No auth token available'));
    }

    const url = `${environment.mindxbkUrl}CustomerConversation/Conversation/${conversationId}/ticket`;
    const headers = new HttpHeaders({
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    });

    const body = { ticketId };

    // PATCH then fetch the updated conversation and emit it to subscribers
    return this.http.patch<any>(url, body, { headers }).pipe(
      switchMap(() => this.getConversationById(conversationId)),
      tap((updated: Conversation) => {
        try {
          this.conversation_subject.next(updated);
        } catch (e) {
          console.error('Error emitting updated conversation', e);
        }
      }),
      catchError(err => {
        console.error('Error updating/fetching conversation after ticket patch', err);
        return throwError(() => err);
      })
    );
  }

  getConversationById(conversationId: string): Observable<Conversation> {
    const token = localStorage.getItem('auth_req');
    if (!token) {
      console.error('❌ No se encontró token en localStorage (auth_req)');
      return throwError(() => new Error('No auth token available'));
    }

    const url = `${environment.mindxbkUrl}CustomerConversation/Conversation/${conversationId}`;
    const headers = new HttpHeaders({
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    });

    return this.http.get<Conversation>(url, { headers }).pipe(
      catchError(err => {
        console.error('Error fetching conversation by id', err);
        return throwError(() => err);
      })
    );
  }


}
